import React from 'react';
import { Box, Typography } from '@mui/material';
import { useStatsTheme } from '../../v2/theme/statsTheme';

/**
 * Card standardisee pour les ecrans cantera.
 * Reprend border/shadow/radius du theme v2.
 *
 * Props:
 *  - title?: string | ReactNode
 *  - subtitle?: string
 *  - headerRight?: ReactNode
 *  - padding?: number | string (default 2)
 *  - tone?: 'dark' | 'light' (default dark immersif)
 *  - elevation?: 'sm' | 'md' | 'lg'
 *  - interactive?: boolean (hover lift)
 *  - onClick?: () => void
 *  - sx?: object
 */
export default function CanteraCard({
  title,
  subtitle,
  headerRight,
  padding = 2,
  tone = 'dark',
  elevation = 'md',
  interactive = false,
  onClick,
  children,
  sx = {},
}) {
  const theme = useStatsTheme();
  const isDark = tone === 'dark';

  const bg = isDark ? theme.surfaces.floating : '#FFFFFF';
  const border = isDark ? theme.surfaces.border : 'rgba(0,0,0,0.08)';
  const fg = isDark ? theme.text.primary : '#111827';
  const fgSecondary = isDark ? theme.text.secondary : '#6B7280';

  const clickable = interactive || !!onClick;

  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: bg,
        border: `1px solid ${border}`,
        borderRadius: `${theme.radius.lg}px`,
        boxShadow: theme.shadows[elevation],
        transition: theme.motion.base,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? {
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows.lg,
          borderColor: theme.accentBorder,
        } : undefined,
        ...sx,
      }}
    >
      {(title || headerRight) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: padding,
            pt: padding,
            pb: subtitle ? 0.5 : 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Typography
                sx={{
                  color: fg,
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography sx={{ color: fgSecondary, fontSize: 12, fontWeight: 500, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerRight}
        </Box>
      )}
      <Box sx={{ p: padding, pt: title || headerRight ? 1 : padding }}>
        {children}
      </Box>
    </Box>
  );
}
